import { ApiIO, getItemsByIds, search } from "common/api";
import { toGetCriteria, toSearchCriteria } from "common/criteria/contact";
import { ActionIO, creators } from "common/picker/action/creator";
import {
  getReducer,
  initState,
  ReducerIO,
  reinitState,
} from "common/picker/reducer";
import { getStore } from "common/picker/store";
import {
  ContactDto,
  SearchCriteria,
  searchProperties,
} from "common/types/contact";
import { TagPicker } from "ic-contact-picker/component/TagPicker";
import { FilterKeyMapper, IO } from "ic-contact-picker/types";
import {
  flatten,
  get,
  isEqual,
  map,
  reduce,
  uniq,
  size,
  isEmpty,
} from "lodash";
import { Component, h } from "preact";
import { connect, Provider } from "preact-redux";
import { bindActionCreators } from "redux";
import { Store } from "redux";

export class Main extends Component<IO<ContactDto>, {}> {
  private store: any;

  public render() {
    this.store = this.initStore(this.props, this.store);

    const actionCreators = this.initActionCreators(this.props);
    const mapStateToProps = (state: any, props: any) => {
      const reducerIO: ReducerIO = {
        capacity: this.props.capacity,
        displaySearchBy: this.props.displaySearchBy,
        placeholder: this.props.placeholder
          ? this.props.placeholder
          : "Search contact",
        selectedSearchBy: this.props.selectedSearchBy,
        state: this.props.state,
        userEmail: this.props.userEmail,
        disabled: size(get(state, "tagList.tags", [])) >= this.props.capacity,
      };
      return reinitState(reducerIO, state);
    };

    const App = connect(mapStateToProps, (dispatch: any) =>
      bindActionCreators(actionCreators as any, dispatch)
    )(TagPicker as any);

    return (
      <Provider store={this.store}>
        <App />
      </Provider>
    );
  }

  public componentDidMount() {
    if (
      this.props.emitOnMount &&
      isEmpty(this.props.selectedContactIds) &&
      isEmpty(this.props.readonlyContactIds)
    ) {
      this.props.onChange([]);
    }
  }

  private initActionCreators = (io: IO<ContactDto>) => {
    const apiIO: ApiIO = {
      baseUrl: (id: string) => `2.0/contacts` + (id ? `/${id}` : ``),
      fetch: io.fetch,
      headers: io.headers,
      serviceUrl: io.soaUrl,
      type: "json",
      ssoV2: io.ssoV2,
      bus: io.bus,
      env: io.env,
    };

    const actionIO: ActionIO<ContactDto, SearchCriteria> = {
      search: (criteria: SearchCriteria) =>
        search<ContactDto[]>(apiIO)({
          ...criteria,
          isIcUser: io.isIcUser,
          ...(io.internalOnly ? { isInternal: io.internalOnly } : {}),
        }),

      startItems: () => {
        const getContactsByIds = getItemsByIds(apiIO);

        const readOnlyItems = getContactsByIds(
          toGetCriteria(uniq(io.readonlyContactIds), searchProperties)
        ).then(items => map(items, (i: any) => ({ ...i, readonly: true })));

        const selectedItems = getContactsByIds(
          toGetCriteria(uniq(io.selectedContactIds), searchProperties)
        );

        const itemsPromise = Promise.all([readOnlyItems, selectedItems]);

        return itemsPromise.then(items => flatten(items)) as Promise<
          ContactDto[]
        >;
      },
      toCriteria: (terms: string) => toSearchCriteria(io.userEmail, terms),

      updatedProps: () => this.props,
      ...(io.filter
        ? {
            filterItems: (contacts: ContactDto[]): ContactDto[] =>
              contacts.filter(contact =>
                reduce(
                  io.filter,
                  (acc: boolean, values: Array<string | number>, key: string) =>
                    acc &&
                    values.indexOf(
                      get(contact, FilterKeyMapper[key]) as string | number
                    ) >= 0,
                  true
                )
              ),
          }
        : {}),
    };

    return creators(actionIO);
  };

  private initStore = (io: IO<ContactDto>, store: Store<any>) => {
    const reducerIO: ReducerIO = {
      capacity: io.capacity,
      displaySearchBy: io.displaySearchBy,
      placeholder: io.placeholder ? io.placeholder : "Search contact",
      selectedSearchBy: io.selectedSearchBy,
      state: io.state,
      userEmail: io.userEmail,
    };

    if (store) {
      const state = reinitState(reducerIO, store.getState());
      const reducer = getReducer(state);
      store.replaceReducer(reducer);
    } else {
      const reducer = getReducer(initState(reducerIO));
      store = getStore(reducer);

      let tags: ContactDto[];
      store.subscribe(() => {
        const state = store.getState();
        if (!isEqual(tags, state.tagList.tags)) {
          tags = state.tagList.tags;
          io.onChange(tags);
        }
      });
    }

    return store;
  };
}
